import { deleteObject, getDownloadURL, ref, uploadBytes, type FirebaseStorage } from 'firebase/storage';
import { addPendingUpload } from './offline-uploads';

export async function uploadFile(storage: FirebaseStorage, file: File, path: string, type = 'image'): Promise<string> {
    if (!file.type.startsWith(`${type}/`)) {
        throw new Error(`File type ${file.type} does not match ${type}`);
    }
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    return getDownloadURL(snapshot.ref);
}

export async function removeFile(storage: FirebaseStorage, path: string): Promise<void> {
    const imageRef = ref(storage, path);
    await deleteObject(imageRef);
}

export async function uploadImage(storage: FirebaseStorage, file: File, path: string): Promise<Image> {
    const fileName = path.split('/').pop();
    const optimizedFile = await optimizeImage(file, fileName, { maxWidth: 1920, maxHeight: 1080 });
    try {
        const url = await uploadFile(storage, optimizedFile, path);
        return {url};
    }
    catch (e) {
        const url = URL.createObjectURL(optimizedFile);
        const tempId = await addPendingUpload(optimizedFile, path);
        return {url, tempId};
    }
}

async function optimizeImage(file: File, fileName: string, options: { maxWidth: number; maxHeight: number; quality?: number }): Promise<File> {
    const { maxWidth, maxHeight, quality = 0.8 } = options;

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;

            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    return reject(new Error('Could not get canvas context'));
                }

                let { width, height } = img;

                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round(height * (maxWidth / width));
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round(width * (maxHeight / height));
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(blob => {
                    if (blob) {
                        resolve(new File([blob], fileName, {type: 'image/webp', lastModified: Date.now()}));
                    }
                    else {
                        reject(new Error('Canvas to Blob conversion failed'));
                    }
                }, 'image/webp', quality);
            };

            img.onerror = (error) => reject(error);
        };

        reader.onerror = (error) => reject(error);
    });
}